// colorUtils.js - Reusable Color Identification Utility

/**
 * Converts hex color to RGB object
 * @param {string} hex - Hex color code (e.g., "#FF0000")
 * @returns {object|null} RGB object {r, g, b} or null if invalid
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Converts RGB object to hex color
 * @param {object} rgb - RGB object {r, g, b}
 * @returns {string} Hex color code
 */
export const rgbToHex = (rgb) => {
  const toHex = (n) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
};

/**
 * Basic color name to hex mapping
 */
const basicColors = {
  // Basic colors
  'red': '#FF0000',
  'blue': '#0000FF',
  'green': '#008000',
  'yellow': '#FFFF00',
  'orange': '#FFA500',
  'purple': '#800080',
  'pink': '#FFC0CB',
  'brown': '#A52A2A',
  'black': '#000000',
  'white': '#FFFFFF',
  'gray': '#808080',
  'grey': '#808080',
  
  // Extended colors
  'cyan': '#00FFFF',
  'magenta': '#FF00FF',
  'lime': '#00FF00',
  'navy': '#000080',
  'teal': '#008080',
  'olive': '#808000',
  'maroon': '#800000',
  'aqua': '#00FFFF',
  'silver': '#C0C0C0',
  'beige': '#F5F5DC',
  'tan': '#D2B48C',
  'gold': '#FFD700',
  'violet': '#EE82EE',
  'indigo': '#4B0082',
  'turquoise': '#40E0D0',
  'coral': '#FF7F50',
  'salmon': '#FA8072',
  'khaki': '#F0E68C',
  'crimson': '#DC143C',
  'lavender': '#E6E6FA',
  'plum': '#DDA0DD',
  'mint': '#98FF98',
  'peach': '#FFDAB9',
  'ivory': '#FFFFF0',
  'chocolate': '#D2691E',
  'bronze': '#CD7F32',
  'copper': '#B87333',
};

/**
 * Modifier keywords that affect color brightness/saturation
 */
const modifierKeywords = ['light', 'dark', 'bright', 'pale', 'deep', 'vivid', 'fluorescent', 'neon'];

/**
 * Apply color modifiers to RGB values
 * @param {object} color - RGB color object {r, g, b}
 * @param {string} modifier - Modifier keyword
 * @returns {object} Modified RGB color object
 */
const applyModifier = (color, modifier) => {
  const result = { ...color };
  
  switch(modifier) {
    case 'light':
    case 'pale':
      result.r = Math.min(255, result.r + 80);
      result.g = Math.min(255, result.g + 80);
      result.b = Math.min(255, result.b + 80);
      break;
      
    case 'dark':
    case 'deep':
      result.r = Math.max(0, result.r - 80);
      result.g = Math.max(0, result.g - 80);
      result.b = Math.max(0, result.b - 80);
      break;
      
    case 'bright':
    case 'vivid':
      result.r = Math.min(255, result.r * 1.3);
      result.g = Math.min(255, result.g * 1.3);
      result.b = Math.min(255, result.b * 1.3);
      break;
      
    case 'fluorescent':
    case 'neon':
      result.r = Math.min(255, result.r * 1.2);
      result.g = Math.min(255, 255);
      result.b = Math.min(255, result.b * 0.8);
      break;
  }
  
  return result;
};

/**
 * Main function to identify color from hex color code
 * @param {string} colorCode - Hex color code (e.g., "#FF0000", "FF0000", "#F00")
 * @returns {string|null} Normalized hex color code or null if invalid
 */
export const identifyColor = (colorCode) => {
  if (!colorCode || typeof colorCode !== 'string') {
    return null;
  }

  // Validate and normalize hex color code
  const normalized = normalizeHexColor(colorCode.trim());
  
  return normalized;
};

/**
 * Get all available basic color names
 * @returns {string[]} Array of color names
 */
export const getAvailableColors = () => {
  return Object.keys(basicColors);
};

/**
 * Get all available modifier keywords
 * @returns {string[]} Array of modifier keywords
 */
export const getAvailableModifiers = () => {
  return [...modifierKeywords];
};

/**
 * Validate if a color name can be identified
 * @param {string} colorName - Color name to validate
 * @returns {boolean} True if color can be identified
 */
export const isValidColorName = (colorName) => {
  return identifyColor(colorName) !== null;
};

/**
 * Get color with additional metadata
 * @param {string} colorName - Color name
 * @returns {object|null} Object containing {name, hex, rgb, isValid}
 */
export const getColorDetails = (colorName) => {
  const hex = identifyColor(colorName);
  
  if (!hex) {
    return {
      name: colorName,
      hex: null,
      rgb: null,
      isValid: false
    };
  }

  const rgb = hexToRgb(hex);
  
  return {
    name: colorName,
    hex: hex,
    rgb: rgb,
    isValid: true
  };
};

/**
 * Helper function to extract color name from strings like "FX1 NAVY" or "FX31 ORANGE"
 * @param {string} colorString - Color string that may contain codes
 * @returns {string} Color name without code prefix
 */
export const extractColorName = (colorString) => {
  if (!colorString || typeof colorString !== 'string') return colorString;
  
  // Remove common color codes (FX1, FX31, CT109, etc.) - pattern: letters/numbers followed by space(s)
  const cleaned = colorString.replace(/^[A-Z0-9]+\s+/i, '').trim();
  
  // If after removing code, we still have text, return it; otherwise return original
  return cleaned || colorString;
};

/**
 * Validates if a string is a valid hex color code
 * @param {string} hex - Potential hex color code
 * @returns {boolean} True if valid hex color
 */
export const isValidHexColor = (hex) => {
  if (!hex || typeof hex !== 'string') return false;
  // Remove # if present and check if it's a valid hex color (3 or 6 digits)
  const cleaned = hex.replace(/^#/, '').toUpperCase();
  return /^[0-9A-F]{3}$|^[0-9A-F]{6}$/.test(cleaned);
};

/**
 * Normalizes hex color code to standard format (#RRGGBB)
 * @param {string} hex - Hex color code (with or without #, 3 or 6 digits)
 * @returns {string|null} Normalized hex color or null if invalid
 */
export const normalizeHexColor = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  
  // Remove # if present
  let cleaned = hex.replace(/^#/, '').toUpperCase();
  
  // Convert 3-digit hex to 6-digit
  if (cleaned.length === 3) {
    cleaned = cleaned.split('').map(char => char + char).join('');
  }
  
  // Validate it's now 6 hex digits
  if (!/^[0-9A-F]{6}$/.test(cleaned)) {
    return null;
  }
  
  return `#${cleaned}`;
};

/**
 * Get hex color code from color data, prioritizing color_code over name-based identification
 * @param {object|string} colorData - Color data object with color_code and/or color, or just a color string
 * @param {string} colorData.color_code - Hex color code (preferred)
 * @param {string} colorData.color - Color name (fallback)
 * @returns {string|null} Hex color code or null if not found
 */
export const getColorHex = (colorData) => {
  // Handle string input (backward compatibility)
  if (typeof colorData === 'string') {
    // Check if it's already a hex color
    const normalized = normalizeHexColor(colorData);
    if (normalized) return normalized;
    // Otherwise treat as color name
    return identifyColor(colorData);
  }
  
  // Handle object input
  if (!colorData || typeof colorData !== 'object') {
    return null;
  }
  
  // Priority 1: Use color_code if available and valid
  if (colorData.color_code) {
    const normalized = normalizeHexColor(colorData.color_code);
    if (normalized) {
      return normalized;
    }
  }
  
  // Priority 2: Extract color name from color field and identify
  if (colorData.color) {
    const extractedColor = extractColorName(colorData.color);
    return identifyColor(extractedColor);
  }
  
  return null;
};


// ============================================
// USAGE EXAMPLES
// ============================================

/*
// Example 1: Basic usage
import { identifyColor } from './colorUtils';

const color = identifyColor('light blue');
console.log(color); // Output: "#53A3D3" (or similar)

// Example 2: Using in React component
import React from 'react';
import { identifyColor } from './colorUtils';

function MyComponent() {
  const handleColorSelect = (colorName) => {
    const hexColor = identifyColor(colorName);
    if (hexColor) {
      // Use the color
      document.body.style.backgroundColor = hexColor;
    }
  };
  
  return (
    <button onClick={() => handleColorSelect('dark red')}>
      Apply Color
    </button>
  );
}

// Example 3: Get color details
import { getColorDetails } from './colorUtils';

const details = getColorDetails('fluorescent green');
console.log(details);
// Output: {
//   name: "fluorescent green",
//   hex: "#00FF00",
//   rgb: { r: 0, g: 255, b: 0 },
//   isValid: true
// }

// Example 4: Validate color name
import { isValidColorName } from './colorUtils';

if (isValidColorName('pink')) {
  console.log('Valid color!');
}

// Example 5: Get all available colors
import { getAvailableColors, getAvailableModifiers } from './colorUtils';

const colors = getAvailableColors();
const modifiers = getAvailableModifiers();
console.log(`You can use: ${colors.join(', ')}`);
console.log(`With modifiers: ${modifiers.join(', ')}`);
*/